// -- [Dealers who trust Drivible - infinite horizontal marquee]

import { useState, useCallback, useRef } from 'react'
import type { Dealership } from '@/types/content'

/** Derives display name from logoSrc: basename, no extension, underscores → spaces */
function displayNameFromLogoSrc(logoSrc: string): string {
  const base = logoSrc.split('/').pop() ?? ''
  const nameWithoutExt = base.replace(/\.[^.]+$/, '')
  return nameWithoutExt.replace(/_/g, ' ')
}

interface DealershipLogosProps {
  dealerships: Dealership[]
}

const DEFAULT_LABEL = 'Trusted by dealerships across Australia'

function DealershipLogo({
  d,
  isActive,
  onActivate,
}: {
  d: Dealership
  isActive: boolean
  onActivate: (d: Dealership) => void
}) {
  const [imgError, setImgError] = useState(false)
  const titleText = displayNameFromLogoSrc(d.logoSrc)
  const handleActivate = useCallback(() => onActivate(d), [d, onActivate])
  return (
    <div
      className={`group flex shrink-0 items-center justify-center px-8 h-[80px] cursor-default rounded-lg transition-all ${
        isActive ? 'ring-2 ring-brand-orange ring-offset-2 ring-offset-gray-50' : ''
      }`}
      title={titleText}
      onMouseEnter={handleActivate}
      onPointerDown={handleActivate}
      aria-label={titleText}
    >
      {imgError ? (
        <span className="text-sm font-medium text-gray-500">{d.name}</span>
      ) : (
        <img
          src={d.logoSrc}
          alt={d.altText ?? titleText}
          width={120}
          height={60}
          className={`max-h-[60px] w-auto object-contain transition-all pointer-events-none ${
            isActive ? 'grayscale-0 opacity-100' : 'grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100'
          }`}
          onError={() => setImgError(true)}
        />
      )}
    </div>
  )
}

export function DealershipLogos({ dealerships }: DealershipLogosProps) {
  if (dealerships.length === 0) return null

  const [activeLogoSrc, setActiveLogoSrc] = useState<string | null>(null)
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onActivate = useCallback((d: Dealership) => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
      leaveTimeoutRef.current = null
    }
    setActiveLogoSrc(d.logoSrc)
  }, [])

  const onMarqueeLeave = useCallback(() => {
    leaveTimeoutRef.current = setTimeout(() => setActiveLogoSrc(null), 200)
  }, [])

  const onMarqueeEnter = useCallback(() => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
      leaveTimeoutRef.current = null
    }
  }, [])

  // -- [Duplicate for seamless infinite loop - scroll resets when second copy is in view]
  const logosDuplicated = [...dealerships, ...dealerships]

  const centerLabelText = activeLogoSrc
    ? displayNameFromLogoSrc(activeLogoSrc)
    : DEFAULT_LABEL

  return (
    <section id="customers" className="py-16 px-0 bg-gray-50 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 mb-10">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Dealers trust Drivible</h2>
        {/* <p className="text-gray-600">Trusted by dealerships across Australia.</p> */}
      </div>
      <div
        className="relative"
        onMouseLeave={onMarqueeLeave}
        onMouseEnter={onMarqueeEnter}
      >
        <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none" />
        <div className="overflow-hidden">
          <div className="flex w-max animate-dealership-marquee">
            {logosDuplicated.map((d, i) => (
              <DealershipLogo
                key={`${d.logoSrc}-${i}`}
                d={d}
                isActive={activeLogoSrc === d.logoSrc}
                onActivate={onActivate}
              />
            ))}
          </div>
        </div>
        {/* -- [Center label: shows hovered/tapped dealership name or default line] */}
        <div className="flex justify-center mt-6 min-h-[2rem]">
          <p
            className="text-gray-600 text-center text-lg font-medium transition-opacity duration-150"
            aria-live="polite"
            aria-atomic="true"
          >
            {centerLabelText}
          </p>
        </div>
      </div>
    </section>
  )
}
